import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { RegistrationStepProps } from '../../RegistrationStep.types';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import {
  resetRegistrationData,
  selectRegistrationConfig,
  selectRegistrationFormData,
  selectShouldReturnToSummary,
  setReturnToSummary,
  setStepIndex,
} from '../../../../state';
import dayjs from 'dayjs';
import { RegistrationData } from '../../../../state/registrationForm/registrationForm.types';
import { Box, Button, Text, ScrollView } from '@betty/moonlight';
import SummaryStepRow from './SummaryStepRow';
import { GenderMap } from '../../../RegistrationField/FieldComponents/GenderField/GenderField';
import { Modal } from '@betty/navigation';
import { AsYouType, CountryCode } from 'libphonenumber-js';
import { IndustriesMap } from '../../../RegistrationField/FieldComponents/IndustriesField/IndustriesField';
import { useStickyButton } from '../../../../hooks/useStickyButton';
import AdditionalRegisterInfo from '../../../AdditionalRegisterInfo/AdditionalRegisterInfo';

const limitPeriods: Array<'Daily' | 'Weekly' | 'Monthly'> = [
  'Daily',
  'Weekly',
  'Monthly',
];

function SummaryStep({ step, onSubmit }: RegistrationStepProps) {
  const dispatch = useDispatch();
  const returnToSummary = useSelector(selectShouldReturnToSummary);
  const config = useSelector(selectRegistrationConfig);
  const data = useSelector(selectRegistrationFormData);

  const {
    buttonAnimatedStyle,
    handleContentLayout,
    handleScrollViewLayout,
    scrollHandler,
  } = useStickyButton({
    buttonHeight: 120,
    extraOffset: 76,
  });

  const phone = useMemo(
    () =>
      `${data.phone.phoneCode} ${new AsYouType(
        data.phone.phoneCountry as CountryCode,
      ).input(data.phone.phoneNumber)}`,
    [data.phone],
  );
  const birthDate = useMemo(
    () => dayjs(data.birthDate).format('MM/DD/YYYY'),
    [data.birthDate],
  );
  const address = useMemo(
    () =>
      `${data.address.unit} ${data.address.buildingNumber} ${data.address.streetName} ${data.address.city}, ${data.address.province}, ${data.address.postalCode}`,
    [data.address],
  );
  const depositLimits = useMemo(
    () =>
      limitPeriods
        .map(
          (period) =>
            `${period}: ${
              data.limits.Deposit[period]
                ? `$${new Intl.NumberFormat().format(data.limits.Deposit[period] ?? 0)}`
                : 'None'
            }`,
        )
        .join(', '),
    [data.limits.Deposit],
  );
  const lossLimits = useMemo(
    () =>
      limitPeriods
        .map(
          (period) =>
            `${period}: ${
              data.limits.Loss[period]
                ? `$${new Intl.NumberFormat().format(data.limits.Loss[period] ?? 0)}`
                : 'None'
            }`,
        )
        .join(', '),
    [data.limits.Loss],
  );

  const onRegistrationLimitsEditPress = useCallback(() => {
    Modal.open({
      namespace: 'auth',
      type: 'RegistrationLimitsModal',
      options: {
        hideHeader: false,
      },
      style: {
        width: {
          mobile: '100%',
          tablet: '100%',
          desktop: 550,
        },
        height: {
          mobile: '100%',
          tablet: '100%',
          desktop: 800,
        },
        paddingTop: 'space-200',
        backgroundColor: 'screenBackground',
      },
    });
  }, []);

  const navigateToField = useCallback(
    (field: keyof RegistrationData) => {
      const stepIndex = config.steps.findIndex(
        (step) => step.fields?.indexOf(field) !== -1,
      );

      if (stepIndex !== -1) {
        dispatch(setReturnToSummary(true));
        dispatch(setStepIndex(stepIndex));
      }
    },
    [config.steps, dispatch],
  );

  const startOver = useCallback(() => {
    dispatch(resetRegistrationData());
    dispatch(setStepIndex(0));
  }, [dispatch]);

  useEffect(() => {
    if (returnToSummary) {
      dispatch(setReturnToSummary(false));
    }
  }, [returnToSummary, dispatch]);

  return (
    <Box flex={1} onLayout={handleScrollViewLayout} width="100%">
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flex: 1 }}
        animated={true}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
      >
        <Box alignItems="center" width="100%" marginBottom="space-1000">
          <Box
            width={{
              mobile: '100%',
              tablet: '100%',
              desktop: 760,
            }}
          >
            <Box
              padding="space-400"
              gap="space-400"
              onLayout={handleContentLayout}
              paddingBottom="space-800"
              marginBottom="space-1200"
            >
              <Box>
                {step.stepTitleNS && step.stepTitleKey ? (
                  <Text
                    variant="h4"
                    color="darkSurfacePrimary"
                    locNS={step.stepTitleNS}
                    locKey={step.stepTitleKey}
                    marginBottom="space-300"
                  />
                ) : null}
                {step.stepDescriptionNS && step.stepDescriptionKey ? (
                  <Text
                    variant="h6"
                    color="darkSurfacePrimary"
                    locNS={step.stepDescriptionNS}
                    locKey={step.stepDescriptionKey}
                    marginBottom="space-400"
                  />
                ) : null}
              </Box>
              <Box>
                <SummaryStepRow
                  locNS="registration"
                  locKey="firstNameLabel"
                  value={data.firstName}
                  onPress={() => navigateToField('firstName')}
                />
                {data.middleName ? (
                  <SummaryStepRow
                    locNS="registration"
                    locKey="middleNameLabel"
                    value={data.middleName}
                    onPress={() => navigateToField('middleName')}
                  />
                ) : null}
                <SummaryStepRow
                  locNS="registration"
                  locKey="lastNameLabel"
                  value={data.lastName}
                  onPress={() => navigateToField('lastName')}
                />
                <SummaryStepRow
                  locNS="registration"
                  locKey="dateOfBirthLabel"
                  value={birthDate}
                  onPress={() => navigateToField('birthDate')}
                />
                <SummaryStepRow
                  locNS="registration"
                  locKey="addressLabel"
                  value={address}
                  onPress={() => navigateToField('address')}
                />
                <SummaryStepRow
                  locNS="registration"
                  locKey="mobilePhoneLabel"
                  value={phone}
                  onPress={() => navigateToField('phone')}
                />
                <SummaryStepRow
                  locNS="registration"
                  locKey="genderLabel"
                  value={GenderMap[data.gender]}
                  onPress={() => navigateToField('gender')}
                />
                <SummaryStepRow
                  locNS="registration"
                  locKey="industryLabel"
                  value={IndustriesMap[data.industry]}
                  onPress={() => navigateToField('industry')}
                />
                <SummaryStepRow
                  locNS="registration"
                  locKey="occupationLabel"
                  value={data.occupation}
                  onPress={() => navigateToField('occupation')}
                />
                {depositLimits ? (
                  <SummaryStepRow
                    locNS="registration"
                    locKey="depositLimitsTitle"
                    value={depositLimits}
                    onPress={onRegistrationLimitsEditPress}
                  />
                ) : null}
                {lossLimits ? (
                  <SummaryStepRow
                    locNS="registration"
                    locKey="lossLimitsTitle"
                    value={lossLimits}
                    onPress={onRegistrationLimitsEditPress}
                  />
                ) : null}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          marginTop={{
            desktop: 'space-0',
            tablet: 'space-0',
            mobile: '-space-200',
          }}
        >
          <AdditionalRegisterInfo />
        </Box>
      </ScrollView>
      <Box
        alignItems="center"
        backgroundColor="screenBackground"
        paddingBottom="space-400"
        marginTop="space-600"
        animatedStyle={buttonAnimatedStyle}
      >
        <Box
          width={{
            mobile: '100%',
            tablet: '100%',
            desktop: 760,
          }}
          paddingHorizontal="space-400"
        >
          <Box flexDirection="column" gap="space-200">
            <Button
              variant="secondary"
              locKey="startOver"
              locNS="registration"
              onPress={startOver}
              size="large"
            />
            <Button
              variant="primary"
              locKey="submit"
              locNS="registration"
              rightIcon="arrowForward"
              onPress={onSubmit}
              size="large"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
export default memo(SummaryStep);
